import { useEffect, useState, useCallback } from "react";
import {
  Loader2, StickyNote, FileDown, Mail, RefreshCw, Link2, Power, PowerOff,
  Check, Clock, CalendarCheck, ChevronRight, Copy,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  const d = r.status === 204 ? null : await r.json().catch(() => null);
  if (!r.ok) throw new Error((d && d.detail) || "Request failed");
  return d;
};

const STEPS = [
  { key: "agreement_signed", label: "Agreement Signed" },
  { key: "payment_received", label: "Payment Received" },
  { key: "welcome_email_sent", label: "Welcome Email Sent" },
  { key: "account_activated", label: "Account Activated" },
  { key: "onboarding_scheduled", label: "Onboarding Scheduled" },
  { key: "onboarding_complete", label: "Onboarding Complete" },
];

function StatusTrack({ rec, compact }) {
  return (
    <div className={`flex ${compact ? "gap-1.5" : "flex-col gap-2"}`}>
      {STEPS.map((s) => {
        const done = !!rec[s.key];
        if (compact)
          return <span key={s.key} title={s.label}
            className="h-2 w-2 rounded-full" style={{ background: done ? "#7ee0a1" : "#3f3f46", boxShadow: done ? "0 0 6px #7ee0a1" : "none" }} />;
        return (
          <div key={s.key} className="flex items-center gap-2.5 text-sm">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: done ? "#7ee0a1" : "#52525b", boxShadow: done ? "0 0 6px #7ee0a1" : "none" }} />
            <span className={done ? "text-zinc-200" : "text-zinc-500"}>{done ? "🟢" : "🟡"} {s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function OnboardingDash() {
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("needs_attention");
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState("");
  const [joinPlans, setJoinPlans] = useState([]);

  useEffect(() => {
    api("/billing/plans")
      .then((d) => setJoinPlans((d.plans || []).filter((p) => p.published && ["subscription", "program"].includes(p.type))))
      .catch(() => {});
  }, []);

  const copyJoinLink = async (planId) => {
    const url = `${window.location.origin}/join/${planId}`;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) toast.success("Onboarding link copied", { description: url });
    else toast.info("Copy this link manually:", { description: url, duration: 12000 });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`/onboarding/admin/records?filter=${filter}`);
      setRecords(d);
      if (sel) setSel(d.find((r) => r.id === sel.id) || null);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [filter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const refreshSel = async (id) => {
    const r = await api(`/onboarding/admin/records/${id}`);
    setSel(r);
    setRecords((rs) => rs.map((x) => (x.id === id ? r : x)));
  };

  const act = async (id, label, fn) => {
    setActing(label);
    try { await fn(); toast.success(`${label} done`); } catch (e) { toast.error(e.message); }
    setActing("");
  };

  const toggleStep = (id, step, value) =>
    act(id, value ? `Marked ${step}` : `Unmarked ${step}`, async () => {
      await api(`/onboarding/admin/records/${id}/step`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ step, value }) });
      await refreshSel(id); await load();
    });

  const addNote = (id) => act(id, "Note added", async () => {
    await api(`/onboarding/admin/records/${id}/note`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: note }) });
    setNote(""); await refreshSel(id);
  });

  const resend = (id, kind, label) => act(id, label, async () => {
    const d = await api(`/onboarding/admin/records/${id}/${kind}`, { method: "POST" });
    if (d && d.payment_url) { await navigator.clipboard.writeText(d.payment_url).catch(() => {}); toast.success("Payment link copied to clipboard"); }
  });

  const activate = (id, on) => act(id, on ? "Activated" : "Deactivated", async () => {
    await api(`/onboarding/admin/records/${id}/${on ? "activate" : "deactivate"}`, { method: "POST" });
    await refreshSel(id); await load();
  });

  return (
    <div data-testid="onboarding-dash">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl">Student Onboarding</h2>
          <p className="text-xs text-zinc-500 mt-1">
            {filter === "needs_attention" ? "Needs Attention — students not yet fully onboarded" : "All students"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white/5 rounded-lg p-1">
            <button onClick={() => setFilter("needs_attention")} data-testid="onb-filter-attention"
              className={`px-3 py-1.5 rounded-md text-xs transition-colors ${filter === "needs_attention" ? "bg-[#FAFAFA] text-[#0A0A0A]" : "text-zinc-400"}`}>Needs Attention</button>
            <button onClick={() => setFilter("all")} data-testid="onb-filter-all"
              className={`px-3 py-1.5 rounded-md text-xs transition-colors ${filter === "all" ? "bg-[#FAFAFA] text-[#0A0A0A]" : "text-zinc-400"}`}>All</button>
          </div>
          <button onClick={load} className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-white" data-testid="onb-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>

      {joinPlans.length > 0 && (
        <div className="mb-6 glass-card p-4" data-testid="onb-join-links">
          <div className="section-label text-[9px] text-zinc-500 mb-2.5 flex items-center gap-1.5">
            <Link2 className="h-3 w-3" /> Shareable Onboarding Links — send a student straight to sign &amp; pay
          </div>
          <div className="flex flex-wrap gap-2">
            {joinPlans.map((p) => (
              <button key={p.id} onClick={() => copyJoinLink(p.id)} data-testid={`onb-copy-link-${p.id}`}
                className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5">
                <Copy className="h-3 w-3" /> {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 text-zinc-500 animate-spin" /></div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6">
          {/* list */}
          <div className="space-y-2">
            {records.length === 0 && <div className="py-16 text-center text-zinc-500 text-sm" data-testid="onb-empty">No students in this view.</div>}
            {records.map((r) => (
              <button key={r.id} onClick={() => setSel(r)} data-testid={`onb-row-${r.id}`}
                className={`w-full text-left glass-card p-4 transition-colors ${sel?.id === r.id ? "border-[#9fd3ff]/40" : "hover:border-white/20"}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{r.full_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{r.email} · {r.plan_name}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0" />
                </div>
                <div className="mt-3"><StatusTrack rec={r} compact /></div>
              </button>
            ))}
          </div>

          {/* detail */}
          <div>
            {!sel ? (
              <div className="glass-card p-8 text-center text-zinc-500 text-sm h-full flex items-center justify-center">Select a student to manage their onboarding.</div>
            ) : (
              <div className="glass-card p-6" data-testid="onb-detail">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading text-lg">{sel.full_name}</div>
                    <div className="text-xs text-zinc-500">{sel.email}</div>
                    <div className="text-xs text-[#9fd3ff] mt-1">{sel.plan_name} · signed {new Date(sel.agreement_signed_at).toLocaleDateString()}</div>
                  </div>
                  <a href={`${API}/onboarding/admin/records/${sel.id}/pdf`} target="_blank" rel="noreferrer"
                    className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5" data-testid="onb-pdf"><FileDown className="h-3.5 w-3.5" /> Agreement PDF</a>
                </div>

                <div className="mt-5 p-4 rounded-lg bg-white/[0.03] border border-white/8"><StatusTrack rec={sel} /></div>

                {/* manual step toggles */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => toggleStep(sel.id, "onboarding_scheduled", !sel.onboarding_scheduled)} data-testid="onb-toggle-scheduled"
                    className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{sel.onboarding_scheduled ? "Unmark scheduled" : "Mark scheduled"}</button>
                  <button onClick={() => toggleStep(sel.id, "onboarding_complete", !sel.onboarding_complete)} data-testid="onb-toggle-complete"
                    className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" />{sel.onboarding_complete ? "Reopen" : "Mark complete"}</button>
                </div>

                {/* actions */}
                <div className="mt-4 pt-4 border-t border-white/8">
                  <div className="section-label text-[9px] text-zinc-500 mb-2">Actions</div>
                  <div className="flex flex-wrap gap-2">
                    <button disabled={!!acting} onClick={() => resend(sel.id, "resend-agreement", "Agreement re-sent")} data-testid="onb-resend-agreement" className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Resend agreement</button>
                    <button disabled={!!acting} onClick={() => resend(sel.id, "resend-welcome", "Welcome email re-sent")} data-testid="onb-resend-welcome" className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Resend welcome</button>
                    {!sel.payment_received && (
                      <button disabled={!!acting} onClick={() => resend(sel.id, "resend-payment", "Payment link generated")} data-testid="onb-resend-payment" className="btn-glass text-white px-3 py-2 text-xs flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Resend payment link</button>
                    )}
                    {sel.account_activated ? (
                      <button disabled={!!acting} onClick={() => activate(sel.id, false)} data-testid="onb-deactivate" className="btn-glass text-[#ff9a9a] px-3 py-2 text-xs flex items-center gap-1.5"><PowerOff className="h-3.5 w-3.5" /> Deactivate</button>
                    ) : (
                      <button disabled={!!acting} onClick={() => activate(sel.id, true)} data-testid="onb-activate" className="btn-glass text-[#7ee0a1] px-3 py-2 text-xs flex items-center gap-1.5"><Power className="h-3.5 w-3.5" /> Activate manually</button>
                    )}
                  </div>
                  {acting && <div className="mt-2 text-[11px] text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {acting}…</div>}
                </div>

                {/* notes */}
                <div className="mt-5 pt-4 border-t border-white/8">
                  <div className="section-label text-[9px] text-zinc-500 mb-2 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Internal Notes</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                    {(sel.notes || []).length === 0 && <p className="text-xs text-zinc-600">No notes yet.</p>}
                    {(sel.notes || []).map((n) => (
                      <div key={n.id} className="text-xs bg-white/[0.03] rounded-lg p-2.5">
                        <div className="text-zinc-300">{n.text}</div>
                        <div className="text-zinc-600 mt-1">{n.by} · {new Date(n.at).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note or reminder…" data-testid="onb-note-input"
                      className="flex-1 bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-3 py-2 text-sm outline-none" />
                    <button disabled={!note.trim() || !!acting} onClick={() => addNote(sel.id)} data-testid="onb-note-add" className="btn-solid px-4 py-2 text-xs disabled:opacity-40"><Check className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
